import React, { useEffect, useId, useRef, useState } from 'react'
import { actorView, auditErrorText, recordView, SUPPORT_LABEL } from '../entry-format'
import { getInsight } from './insights-api'
import { pivotable } from '../filters-model'
import { formatCount } from '../../sso-workspace/format'
import { dateText } from '../../date/local-time'

// What every Insights panel shares: the card, its one read, and how people
// and records are named in a chart.

// Every count on Insights is written by the shared formatter ("52,679").
export const n0 = formatCount
export const pct = (v, total) => {
  if (!total) return '0%'
  const p = (v / total) * 100
  if (p > 0 && p < 0.1) return '<0.1%'
  return `${p > 0 && p < 1 ? p.toFixed(1) : Math.round(p)}%`
}
export const plural = (n, one, many) => `${n0(n)} ${Number(n) === 1 ? one : many}`
// "once", "twice", "5 times".
export const timesText = n => (Number(n) === 1 ? 'once' : Number(n) === 2 ? 'twice' : `${n0(n)} times`)
export const DAYN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const H = 3600e3
export const hh = h => {
  const m = Math.round(h * 60)
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`
}
// "Tue, Sep 29, 2026" for a day (YYYY-MM-DD): a calendar date as the
// server counted it (in the reader's zone), written as is.
export const dayText = (day, year = true) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: year ? 'numeric' : undefined, timeZone: 'UTC' })
export const esc = s => String(s == null ? '' : s).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
export const hours = ms => ms / H

// The card (mockup .ipanel): the question as the heading's description,
// optional tools beside it, then the body. period: the window the panel
// counts (the page's range, "Last 7 days"), shown beside the title so the
// reader never has to find it in a hint. busy: a newer read is on its way;
// the last answer stays (dimmed) so the page keeps its height and the
// reader's place.
export const Panel = ({ title, question, period, wide, tools, children, bodyRef, busy = false }) => {
  const id = useId()
  return (
    <section className={`au-ipanel${wide ? ' au-wide' : ''}`} aria-labelledby={id} aria-busy={busy || undefined}>
      <div className="au-ip-head">
        <div>
          <div className="au-ip-title"><h3 id={id}>{title}</h3>{period && <span className="au-period">{period}</span>}</div>
          {question && <p className="au-q">{question}</p>}
        </div>
        {tools}
      </div>
      <div className={`au-ip-body${busy ? ' au-busy' : ''}`} ref={bodyRef}>{children}</div>
    </section>
  )
}

// A panel over Task 10's cap (insightCap) counts the newest entries only.
export const TRUNCATED_TEXT = 'Showing the newest 200,000 entries only. Narrow the filters to count them all.'
export const Truncated = ({ data }) => (data && data.truncated ? <p className="sw-hint au-note" role="status">{TRUNCATED_TEXT}</p> : null)

// Loading, or the panel's own refusal; null once there is data.
export const PanelState = ({ state }) => {
  if (state.error) return <p className="au-ins-error" role="alert">{state.error}</p>
  if (state.data === undefined) return <p className="sw-hint">Loading…</p>
  return null
}

// The params a panel sends: the timeline's own (the page's range
// included, which every panel counts), plus extra. navigator: the activity
// calendar, which always reads its own twelve months (it is how a reader
// picks days) and only outlines the page's range.
export const panelParams = (params, navigator, extra) => {
  const p = { ...(params || {}), ...(extra || {}) }
  if (navigator) {
    delete p.from
    delete p.to
  }
  return p
}

// One panel's read: one getInsight call per change of its params (compared
// by value) -- never a background request. A response for parameters the
// panel has moved on from never lands, and nothing lands after unmount.
// state.data: undefined while the first read loads, null when the read
// failed. While a newer read loads the last answer stays, with busy set,
// so a filter or range change never collapses the page (which would drop
// the reader's scroll position to the bottom of the page).
export const useInsight = (panel, params, { navigator = false, extra, fallback = "This panel couldn't be loaded." } = {}) => {
  const key = JSON.stringify(panelParams(params, navigator, extra))
  const [state, setState] = useState({ data: undefined, error: null, busy: false })
  const gen = useRef(0)
  useEffect(() => {
    const mine = ++gen.current
    setState(s => (s.data ? { ...s, busy: true } : { data: undefined, error: null, busy: false }))
    getInsight(panel, JSON.parse(key)).then(
      data => mine === gen.current && setState({ data: data || {}, error: null, busy: false }),
      err => mine === gen.current && setState({ data: null, error: auditErrorText(err, fallback), busy: false })
    )
  }, [panel, key, fallback])
  useEffect(() => () => {
    gen.current++
  }, [])
  return state
}

// The range a panel names when the page gave none (a panel on its own).
export const NO_RANGE = { label: 'Last 30 days', span: 'in the last 30 days', day: null }

// The retention note for a panel whose range starts before what is kept
// (views kept viewsDays, course launches launchesDays; Task 8), or null.
// kind: 'views' or 'launches'. retention: the organization's periods, as
// the calendar and launches reads return them; from: the range's start.
const KEPT = { views: ['viewsDays', 'Views are', 'views'], launches: ['launchesDays', 'Course launches are', 'launches'] }
export const retentionNote = (kind, retention, from, now = Date.now()) => {
  const [field, lead, what] = KEPT[kind]
  const days = retention && retention[field]
  if (!days || !from) return null
  const since = now - days * 864e5
  if (Date.parse(from) >= since) return null
  const day = dateText(since)
  return `${lead} kept ${n0(days)} days, so ${what} before ${day} aren't counted.`
}
export const RetentionNote = ({ kind, retention, from }) => {
  const text = retentionNote(kind, retention, from)
  return text ? <p className="sw-hint au-note au-kept" role="note">{text}</p> : null
}

/* ---- names (the ledger's status rules) ---- */

export const LEFT_SUFFIX = ' (no longer in this organization)'
const RECORD_SUFFIX = { moved: ' (moved)', deleted: ' (deleted)' }

// A person as Insights ships one ({key, name, email, status}): staff are
// EducationXR support (no name, email or id); "other" is Everyone else, the
// people a chart doesn't show by name; a person who left shows as they
// were, marked so. pivot: the actor filter key (actor= lists a person who
// left too; user: and login: keys alike, which the server accepts for
// every reader), or null for staff, Everyone else and unattributed
// changes, which no filter names.
export const personView = (p, { sys = false } = {}) => {
  const key = (p && p.key) || ''
  if (!p || key === 'support' || p.support || p.kind === 'support') return { text: SUPPORT_LABEL, pivot: null, left: false, support: true }
  if (key === 'other') return { text: 'Everyone else', pivot: null, left: false }
  if (key === 'none' || !key) return { text: 'Unattributed change', pivot: null, left: false }
  if (key.startsWith('auto:')) return { text: actorView({ automatic: true, label: key.slice(5) }).text, pivot: key, left: false }
  const [kind, id] = key.split(':')
  const actor = { kind: 'user', name: p.name, email: p.email, status: p.status, ...(kind === 'login' ? { loginID: id } : { userID: id }) }
  const view = actorView(actor, { sys })
  const left = p.status === 'left'
  const pivot = (kind === 'user' || kind === 'login') && pivotable({ actors: [key] }) ? key : null
  return { text: left ? `${view.text}${LEFT_SUFFIX}` : view.text, pivot, left }
}

// A record (a Ref): its last name, marked moved or deleted.
export const recordName = ref => {
  const view = recordView(ref)
  return `${view.text}${RECORD_SUFFIX[ref && ref.status] || ''}`
}

// A pivot patch narrows the timeline to what a chart element counted. null
// means no filter names it (staff, everyone else), so the element opens
// nothing (a count shown is what clicking it lists).
//
// A record's pivot is content's record filter (recordFilter=<type>:<id>):
// it goes with any scope and finds a moved or deleted record among the
// reader's own rows. A staff member's record has no id for a customer, and
// no click.
export const recordKey = ref => (ref && ref.type && ref.id ? `${ref.type}:${ref.id}` : null)
export const recordPivot = ref => (recordKey(ref) ? checked({ recordFilters: [recordKey(ref)] }) : null)

// A patch only when the URL and the server read every value back exactly
// (filters-model pivotable); else null: no click.
export const checked = patch => (patch && pivotable(patch) ? patch : null)

// Two pivot patches as one (lists joined), for a band between two nodes;
// null when either end can't be listed exactly.
export const mergePatch = (a, b) => {
  if (!a || !b) return null
  const out = { ...a }
  Object.entries(b || {}).forEach(([k, v]) => {
    if (Array.isArray(v) && Array.isArray(out[k])) out[k] = [...new Set([...out[k], ...v])]
    else out[k] = v
  })
  return out
}
export const isEmptyPatch = p => !p || Object.keys(p).length === 0
// Every step's patch as one (null when any step can't be listed exactly).
export const pathPatch = steps => checked(steps.reduce((acc, st) => mergePatch(acc, st.patch), {}))

// Chart node names must be unique; the same text in two columns (a record
// type and a record both "Group") gets an invisible mark.
export const makeNamer = () => {
  const used = new Map()
  return (text, id) => {
    let name = text || '—'
    while (used.has(name) && used.get(name) !== id) name += '​'
    used.set(name, id)
    return name
  }
}
